import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, CircularProgress, LinearProgress, Paper, Stack, Tab, Tabs, Typography } from '@mui/material'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import UserLayout from '../../layouts/UserLayout'
import { useSelector } from 'react-redux'
import { api } from '../../utils/api'
import html2pdf from "html2pdf.js";
import { apiBase } from '../../services/apiBase'
import cashflowService from '../../services/cashflowService'
import {
	canManageCashflowAccounts,
	canManageCashflowConfig,
	canManageCashflowExpense,
	canSubmitCashflowReport,
} from '../../utils/cashflowPermissions'
import { mainGreen } from '../../utils/color'
import ConfigTab from './components/ConfigTab'
import ExpensesTab from './components/ExpensesTab'
import ImageViewerDialog from './components/ImageViewerDialog'
import IncomesTab from './components/IncomesTab'
import OrderAccountAssignment from './components/OrderAccountAssignment'
import PaymentConfirmationsTab, { PaymentRequestDialog } from './components/PaymentConfirmationsTab'
import ReportTab from './components/ReportTab'
import StoreDateFilter from './components/StoreDateFilter'
import './cashflow.css'
import {
	accountTypeForOrder,
	adminRoles,
	categoryAllowedSources,
	categoryAllowsDuplicateOrders,
	categoryAllowsOrders,
	categoryAllowsSource,
	DEFAULT_CASH_SOURCE,
	DEFAULT_OTHER_EXPENSE_CATEGORY_NAME,
	DEFAULT_SHIPPING_EXPENSE_CATEGORY_NAME,
	DEFAULT_WITHDRAW_EXPENSE_CATEGORY_NAME,
	hasValue,
	orderCode,
	orderNeedsManualBankAccount,
	parseMoney,
	resolveImageUrl,
	SHIPPING_EXPENSE_NAME,
	summaryMatchesDate,
	todayInputValue,
	toId,
} from './cashflowUtils'

const EMPTY_CATEGORY_FORM = {
	name: '',
	allowOrderSelection: true,
	allowDuplicateOrders: true,
	allowedSources: ['cash', 'bank'],
	requiresApproval: false,
}

// Đối soát cuối ngày cho phép nhiều ảnh, dữ liệu cũ chỉ có 1 ảnh ở field screenshot
const savedScreenshots = (verification) => {
	if (!verification) return null
	if (Array.isArray(verification.screenshots) && verification.screenshots.length > 0) return verification.screenshots
	return verification.screenshot ? [verification.screenshot] : []
}

const cashClosingVerified = (report) => {
	const verification = report?.cashVerification || {}
	const systemCash = parseMoney(report?.systemClosingBalances?.cash)
	if (verification.closingVerified) return true
	return (
		report?.status === 'submitted' &&
		hasValue(verification.actualBalance) &&
		(parseMoney(verification.systemBalance) === systemCash || Boolean(verification.screenshot) || Boolean(verification.explanation))
	)
}

export default function Cashflow() {
	const { currentUser } = useSelector((state) => state.currentUser)
	const [tab, setTab] = useState(0)
	const [date, setDate] = useState(todayInputValue())
	const [stores, setStores] = useState([])
	const [selectedStoreId, setSelectedStoreId] = useState(currentUser.store || '')
	const [loading, setLoading] = useState(false)
	const [summary, setSummary] = useState(null)
	const [orderStatistics, setOrderStatistics] = useState(null)
	const [allBankAccounts, setAllBankAccounts] = useState([])
	const [expenseCategories, setExpenseCategories] = useState([])
	const [exporting, setExporting] = useState(false);
	const [bankForm, setBankForm] = useState({
		name: '',
		accountType: 'bank',
		bankName: '',
		accountName: '',
		accountNumber: '',
		note: '',
	})
	const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY_FORM)
	const [editingCategoryId, setEditingCategoryId] = useState('')
	const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
	const [expenseForm, setExpenseForm] = useState({
		type: 'other',
		category: '',
		bankAccount: '',
		amount: '',
		content: '',
		orderIds: [],
		photos: [],
		existingPhotos: [],
	})
	const [editingExpenseId, setEditingExpenseId] = useState('')
	const [expenseDialogOpen, setExpenseDialogOpen] = useState(false)
	const [incomeForm, setIncomeForm] = useState({
		receiptType: 'abnormal',
		bankAccount: '',
		amount: '',
		content: '',
		appliesToReportDate: date,
		photos: [],
		existingPhotos: [],
	})
	const [editingIncomeId, setEditingIncomeId] = useState('')
	const [incomeDialogOpen, setIncomeDialogOpen] = useState(false)
	const [paymentRequestForm, setPaymentRequestForm] = useState({
		orderId: '',
		bankAccount: '',
		amount: '',
		content: '',
	})
	const [paymentRequestDialogOpen, setPaymentRequestDialogOpen] = useState(false)
	const [paymentSourceAccounts, setPaymentSourceAccounts] = useState([])
	const [reportForm, setReportForm] = useState({
		cashActualBalance: '',
		cashExplanation: '',
		cashScreenshots: [],
		cashFiles: [],
		bankVerifications: {},
		bankOpeningFiles: {},
		bankFiles: {},
	})
	const [savingAccountId, setSavingAccountId] = useState('')
	const [bankOpeningPreviewUrls, setBankOpeningPreviewUrls] = useState({})
	const [bankPreviewUrls, setBankPreviewUrls] = useState({})
	const [cashPreviewUrls, setCashPreviewUrls] = useState([])
	const [imageViewer, setImageViewer] = useState({ open: false, url: '', title: '' })
	const bankOpeningPreviewUrlsRef = useRef({})
	const bankPreviewUrlsRef = useRef({})
	const cashPreviewUrlsRef = useRef([])
	const cashflowLoadRequestRef = useRef(0)
	const reportFormContextRef = useRef('')

	const revokePreviewUrls = useCallback(() => {
		cashPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
		Object.values(bankPreviewUrlsRef.current).forEach((urls) => urls.forEach((url) => URL.revokeObjectURL(url)))
		Object.values(bankOpeningPreviewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url))
		cashPreviewUrlsRef.current = []
		bankPreviewUrlsRef.current = {}
		bankOpeningPreviewUrlsRef.current = {}
		setCashPreviewUrls([])
		setBankPreviewUrls({})
		setBankOpeningPreviewUrls({})
	}, [])

	const isAdminLike = adminRoles.includes(currentUser.role)
	// Ảnh đối soát chỉ được hiển thị khi số liệu đang có đúng là của ngày đang chọn,
	// tránh việc đổi ngày xong vẫn còn thấy ảnh của ngày trước trong lúc chờ tải lại
	const showsSelectedDateImages = summaryMatchesDate(summary, date)
	// Số liệu đang hiển thị đúng là của cửa hàng/ngày đang chọn hay chưa
	const summaryMatchesSelection =
		showsSelectedDateImages && (!toId(summary?.store) || String(toId(summary.store)) === String(selectedStoreId))
	// Chỉ thay cả trang bằng spinner khi chưa có số liệu để hiển thị.
	// Tải lại sau khi cập nhật đối soát thì giữ nguyên nội dung, tránh cảm giác reload trang và nhảy về đầu trang
	const showFullPageLoading = loading && !summaryMatchesSelection
	const isRefreshing = loading && summaryMatchesSelection
	// Qua ngày là khóa toàn bộ thao tác thu chi, kể cả admin
	const isDateLocked = date < todayInputValue()
	const canManageAccounts = canManageCashflowAccounts(currentUser)
	const canManageConfig = canManageCashflowConfig(currentUser)
	const canManageExpense = canManageCashflowExpense(currentUser) && !isDateLocked
	const canApproveExpense = currentUser.role === 'admin' && !isDateLocked
	const canSubmitReport = canSubmitCashflowReport(currentUser) && !isDateLocked

	const selectedStoreName = useMemo(() => {
		if (!isAdminLike) return currentUser.storeName
		return stores.find((store) => store._id === selectedStoreId)?.name || ''
	}, [currentUser.storeName, isAdminLike, selectedStoreId, stores])

	const incomeOrders = useMemo(
		() =>
			summary?.orders?.incomeOrders || [
				...(summary?.orders?.cashOrders || []),
				...(summary?.orders?.transferOrders || []),
			],
		[summary],
	)
	const prepaidOffsetByOrder = useMemo(() => summary?.income?.prepaidOffsetByOrder || {}, [summary])
	const getOrderRemainingReceivable = useCallback(
		(order) => {
			const offset = prepaidOffsetByOrder[String(toId(order))]?.amount || 0
			// Tổng tiền đơn đã gồm phí ship nên không cộng shippingTotal vào nữa
			return Math.max(parseMoney(order.totalPrice) - parseMoney(offset), 0)
		},
		[prepaidOffsetByOrder],
	)
	const confirmedOutgoingPaymentByOrderId = useMemo(() => {
		const result = {}
		summary?.paymentConfirmations?.outgoingConfirmed?.forEach((confirmation) => {
			const orderId = toId(confirmation.order)
			if (orderId) {
				result[orderId] = confirmation
			}
		})
		return result
	}, [summary])
	const unclassifiedIncomeOrderCount = useMemo(
		() =>
			incomeOrders.filter(
				(order) =>
					!confirmedOutgoingPaymentByOrderId[toId(order)] &&
					orderNeedsManualBankAccount(order) &&
					!toId(order.cashflowBankAccount) &&
					getOrderRemainingReceivable(order) > 0,
			).length,
		[incomeOrders, confirmedOutgoingPaymentByOrderId, getOrderRemainingReceivable],
	)
	const pendingApprovalExpenseCount = summary?.expenses?.pendingApprovalItems?.length || 0
	const classificationOrders = useMemo(
		() =>
			[...incomeOrders].sort((first, second) => {
				const firstConfirmedElsewhere = Boolean(confirmedOutgoingPaymentByOrderId[toId(first)])
				const secondConfirmedElsewhere = Boolean(confirmedOutgoingPaymentByOrderId[toId(second)])
				const firstNeedsAccount =
					!firstConfirmedElsewhere &&
					orderNeedsManualBankAccount(first) &&
					!toId(first.cashflowBankAccount) &&
					getOrderRemainingReceivable(first) > 0
				const secondNeedsAccount =
					!secondConfirmedElsewhere &&
					orderNeedsManualBankAccount(second) &&
					!toId(second.cashflowBankAccount) &&
					getOrderRemainingReceivable(second) > 0
				if (firstNeedsAccount !== secondNeedsAccount) return firstNeedsAccount ? -1 : 1
				if (firstConfirmedElsewhere !== secondConfirmedElsewhere) return firstConfirmedElsewhere ? 1 : -1
				return orderCode(first).localeCompare(orderCode(second), 'vi', { numeric: true })
			}),
		[incomeOrders, confirmedOutgoingPaymentByOrderId, getOrderRemainingReceivable],
	)
	const expenseOrderOptions = useMemo(
		() =>
			summary?.orders?.shippingExpenseOrders || summary?.orders?.incomeOrders || summary?.orders?.shippingOrders || [],
		[summary],
	)
	const shippingOrderExpenseMap = useMemo(() => {
		const result = {}
		summary?.expenses?.shippingExpenses?.forEach((expense) => {
			expense.orders?.forEach((order) => {
				result[toId(order)] = expense
			})
		})
		return result
	}, [summary])
	const selectedShippingIncome = useMemo(
		() =>
			expenseOrderOptions
				.filter((order) => expenseForm.orderIds.includes(order._id))
				.reduce((total, order) => total + parseMoney(order.shippingTotal), 0),
		[expenseForm.orderIds, expenseOrderOptions],
	)
	const selectedExpenseOrders = useMemo(
		() => expenseOrderOptions.filter((order) => expenseForm.orderIds.includes(order._id)),
		[expenseForm.orderIds, expenseOrderOptions],
	)
	const transferredOrders = useMemo(
		() => incomeOrders.filter((order) => toId(order.transferStore?.fromStore)),
		[incomeOrders],
	)
	// Tài khoản tạm ngưng chỉ hiện ở tab cấu hình, các ô chọn tài khoản chỉ dùng tài khoản đang bật
	const bankAccounts = useMemo(() => allBankAccounts.filter((account) => account.active !== false), [allBankAccounts])
	const activeExpenseCategories = useMemo(
		() => expenseCategories.filter((category) => category.active !== false),
		[expenseCategories],
	)
	const otherExpenseCategories = useMemo(
		() => activeExpenseCategories.filter((category) => category.type === 'other'),
		[activeExpenseCategories],
	)
	const shippingExpenseCategories = useMemo(
		() => activeExpenseCategories.filter((category) => category.type === 'shipping'),
		[activeExpenseCategories],
	)
	const cashoutExpenseCategories = useMemo(
		() => activeExpenseCategories.filter((category) => category.type === 'cashout'),
		[activeExpenseCategories],
	)
	const hasDefaultExpenseCategory = useMemo(() => {
		const hasName = (categories, name) =>
			categories.some((category) => category.name?.trim().toLowerCase() === name.toLowerCase())
		return (
			hasName(shippingExpenseCategories, DEFAULT_SHIPPING_EXPENSE_CATEGORY_NAME) &&
			hasName(otherExpenseCategories, DEFAULT_OTHER_EXPENSE_CATEGORY_NAME) &&
			hasName(cashoutExpenseCategories, DEFAULT_WITHDRAW_EXPENSE_CATEGORY_NAME)
		)
	}, [cashoutExpenseCategories, otherExpenseCategories, shippingExpenseCategories])
	const selectedExpenseCategory = useMemo(
		() => expenseCategories.find((category) => category._id === expenseForm.category),
		[expenseCategories, expenseForm.category],
	)
	const categoryOrderExpenseMap = useMemo(() => {
		const result = {}
		summary?.expenses?.items?.forEach((expense) => {
			const categoryId = toId(expense.category)
			if (expense.type === 'shipping' || !categoryId) return
			expense.orders?.forEach((order) => {
				const orderId = toId(order)
				if (!orderId) return
				result[categoryId] = result[categoryId] || {}
				result[categoryId][orderId] = expense
			})
		})
		return result
	}, [summary])
	const getLockedOrderExpense = useCallback(
		(orderId, type = expenseForm.type, categoryId = expenseForm.category) => {
			let assignedExpense = null
			if (type === 'shipping') {
				assignedExpense = shippingOrderExpenseMap[orderId]
			} else {
				const category = expenseCategories.find((item) => item._id === categoryId)
				if (!category || categoryAllowsDuplicateOrders(category)) return null
				assignedExpense = categoryOrderExpenseMap[categoryId]?.[orderId]
			}
			return assignedExpense && toId(assignedExpense) !== editingExpenseId ? assignedExpense : null
		},
		[
			categoryOrderExpenseMap,
			editingExpenseId,
			expenseCategories,
			expenseForm.category,
			expenseForm.type,
			shippingOrderExpenseMap,
		],
	)
	const getAccountsForOrder = useCallback(
		(order) =>
			orderNeedsManualBankAccount(order)
				? bankAccounts.filter((account) => (account.accountType || 'bank') === accountTypeForOrder(order))
				: [],
		[bankAccounts],
	)

	const showError = (error) => {
		alert(error?.response?.data?.message || error?.message || 'Có lỗi xảy ra')
	}

	const loadStores = useCallback(async () => {
		if (!isAdminLike) return
		try {
			const data = await apiBase.get(api.GET_STORES_ADMIN)
			setStores(data)
			setSelectedStoreId((prev) => prev || data?.[0]?._id || '')
		} catch (error) {
			showError(error)
		}
	}, [isAdminLike])

	const loadCashflow = useCallback(async () => {
		if (!selectedStoreId) return
		const requestId = cashflowLoadRequestRef.current + 1
		cashflowLoadRequestRef.current = requestId
		const requestedStoreId = selectedStoreId
		const requestedDate = date
		try {
			setLoading(true)
			const [summaryData, accountsData, categoriesData, statisticsData] = await Promise.all([
				cashflowService.getSummary({ storeId: requestedStoreId, date }),
				cashflowService.getBankAccounts({ storeId: requestedStoreId, includeInactive: true }),
				cashflowService.getExpenseCategories({ storeId: requestedStoreId }),
				// Bảng đối chiếu là phần phụ và cần quyền xem thống kê riêng,
				// thiếu quyền thì chỉ ẩn bảng chứ không được làm hỏng báo cáo
				cashflowService
					.getOrderStatistics({ storeId: requestedStoreId, date: requestedDate, isAdminLike })
					.catch(() => null),
			])
			if (requestId !== cashflowLoadRequestRef.current) return
			const responseStoreId = String(toId(summaryData?.store) || '')
			if (responseStoreId && responseStoreId !== requestedStoreId) {
				setSummary(null)
				setAllBankAccounts([])
				setExpenseCategories([])
				setOrderStatistics(null)
				throw new Error('Dữ liệu thu chi trả về không đúng cửa hàng đang chọn, vui lòng tải lại')
			}
			if (summaryData?.reportDate && summaryData.reportDate !== requestedDate) {
				setSummary(null)
				setAllBankAccounts([])
				setExpenseCategories([])
				setOrderStatistics(null)
				throw new Error('Dữ liệu thu chi trả về không đúng ngày đang chọn, vui lòng tải lại')
			}
			setSummary(summaryData)
			setAllBankAccounts(accountsData)
			setExpenseCategories(categoriesData)
			setOrderStatistics(statisticsData)
		} catch (error) {
			if (requestId === cashflowLoadRequestRef.current) {
				showError(error)
			}
		} finally {
			if (requestId === cashflowLoadRequestRef.current) {
				setLoading(false)
			}
		}
	}, [date, isAdminLike, selectedStoreId])

	useEffect(() => {
		loadStores()
	}, [loadStores])

	useEffect(() => {
		loadCashflow()
	}, [loadCashflow])

	useEffect(() => {
		if (!summary) return
		const existingReport = summary.existingReport
		const hasSavedCashClosing = cashClosingVerified(existingReport)
		// Đổi ngày (hoặc đổi cửa hàng) là sang một phiếu đối soát khác nên phải bỏ hết
		// số/ghi chú/ảnh đang gõ dở, chỉ giữ bản nháp khi vẫn còn ở đúng phiếu cũ
		const reportContext = `${toId(summary.store) || ''}|${summary.reportDate || date}`
		const keepDraft = reportFormContextRef.current === reportContext
		reportFormContextRef.current = reportContext
		setReportForm((prev) => {
			const prevBankVerifications = (keepDraft && prev.bankVerifications) || {}
			const nextBankVerifications = {}
			summary.closingBalances?.banks?.forEach((bankBalance) => {
				const accountId = bankBalance.bankAccount._id
				const existing = existingReport?.bankVerifications?.find((item) => toId(item.bankAccount) === accountId)
				const prevVerification = prevBankVerifications[accountId] || {}
				nextBankVerifications[accountId] = {
					openingActualBalance: existing?.openingActualBalance ?? bankBalance.openingBalance ?? '',
					openingExplanation: existing?.openingExplanation || '',
					openingScreenshot: existing?.openingScreenshot || '',
					// Giữ giá trị người dùng đang gõ nếu server chưa lưu cuối ngày cho tài khoản này
					actualBalance:
						existing?.actualBalance !== undefined && existing?.actualBalance !== null
							? existing.actualBalance
							: (prevVerification.actualBalance ?? ''),
					explanation: existing?.explanation || prevVerification.explanation || '',
					screenshots: savedScreenshots(existing) ?? prevVerification.screenshots ?? [],
				}
			})
			return {
				...prev,
				cashActualBalance: hasSavedCashClosing ? existingReport?.cashVerification?.actualBalance ?? '' : '',
				cashExplanation: existingReport?.cashVerification?.explanation || '',
				cashScreenshots: savedScreenshots(existingReport?.cashVerification) || [],
				cashFiles: [],
				bankVerifications: nextBankVerifications,
				bankOpeningFiles: {},
				bankFiles: {},
			}
		})
		revokePreviewUrls()
	}, [date, revokePreviewUrls, summary])

	useEffect(() => revokePreviewUrls, [revokePreviewUrls])

	// Chỉ mặc định về tiền mặt khi khoản chi đang chọn cho phép chi tiền mặt
	useEffect(() => {
		if (!expenseForm.bankAccount && categoryAllowsSource(selectedExpenseCategory, 'cash')) {
			setExpenseForm((prev) => ({ ...prev, bankAccount: DEFAULT_CASH_SOURCE }))
		}
	}, [expenseForm.bankAccount, selectedExpenseCategory])

	useEffect(() => {
		if (!incomeForm.bankAccount) {
			setIncomeForm((prev) => ({ ...prev, bankAccount: DEFAULT_CASH_SOURCE }))
		}
	}, [incomeForm.bankAccount])

	useEffect(() => {
		if (
			expenseForm.type !== 'shipping' &&
			selectedExpenseCategory &&
			!categoryAllowsOrders(selectedExpenseCategory) &&
			expenseForm.orderIds.length > 0
		) {
			setExpenseForm((prev) => ({ ...prev, orderIds: [] }))
		}
	}, [expenseForm.orderIds.length, expenseForm.type, selectedExpenseCategory])

	useEffect(() => {
		setCategoryForm(EMPTY_CATEGORY_FORM)
		setEditingCategoryId('')
	}, [selectedStoreId])

	const saveBankAccount = async () => {
		if (!canManageAccounts) return false
		if (!bankForm.name.trim()) {
			alert('Vui lòng nhập tên tài khoản')
			return false
		}
		try {
			await cashflowService.createBankAccount({ ...bankForm, storeId: selectedStoreId })
			setBankForm({ name: '', accountType: 'bank', bankName: '', accountName: '', accountNumber: '', note: '' })
			await loadCashflow()
			return true
		} catch (error) {
			showError(error)
			return false
		}
	}

	// Xoá hẳn chỉ dùng cho tài khoản gõ nhầm, server chặn nếu tài khoản đã có số liệu
	const deleteBankAccount = async (accountId) => {
		if (!canManageAccounts) return
		if (!window.confirm('Xoá hẳn tài khoản này? Tài khoản đã có số liệu thu chi thì chỉ tạm ngưng được.')) return
		try {
			await cashflowService.deleteBankAccount(accountId)
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	// Tạm ngưng chỉ ẩn tài khoản khỏi các ô chọn, số liệu cũ vẫn giữ nguyên nên bật lại được
	const setBankAccountActive = async (accountId, active) => {
		if (!canManageAccounts) return
		if (!window.confirm(active ? 'Dùng lại tài khoản này?' : 'Tạm ngưng dùng tài khoản này?')) return
		try {
			await cashflowService.updateBankAccount(accountId, { active })
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const resetCategoryForm = () => {
		setCategoryForm(EMPTY_CATEGORY_FORM)
		setEditingCategoryId('')
	}

	const openCreateExpenseCategory = () => {
		resetCategoryForm()
		setCategoryDialogOpen(true)
	}

	const closeCategoryDialog = () => {
		setCategoryDialogOpen(false)
		resetCategoryForm()
	}

	const saveExpenseCategory = async () => {
		if (!canManageConfig) return
		if (!categoryForm.name.trim()) {
			alert('Vui lòng nhập tên khoản chi')
			return
		}
		if ((categoryForm.allowedSources || []).length === 0) {
			alert('Vui lòng chọn nguồn tiền cho khoản chi')
			return
		}
		try {
			const allowOrderSelection = Boolean(categoryForm.allowOrderSelection)
			const payload = {
				name: categoryForm.name.trim(),
				storeId: selectedStoreId,
				allowOrderSelection,
				allowDuplicateOrders: allowOrderSelection ? Boolean(categoryForm.allowDuplicateOrders) : false,
				allowedSources: categoryForm.allowedSources,
				requiresApproval: Boolean(categoryForm.requiresApproval),
			}
			if (editingCategoryId) {
				await cashflowService.updateExpenseCategory(editingCategoryId, payload)
			} else {
				await cashflowService.createExpenseCategory(payload)
			}
			setCategoryDialogOpen(false)
			resetCategoryForm()
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const createDefaultExpenseCategories = async () => {
		if (!canManageConfig) return
		try {
			await cashflowService.createDefaultExpenseCategories({ storeId: selectedStoreId })
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const startEditExpenseCategory = (category) => {
		setEditingCategoryId(category._id)
		const allowOrderSelection = categoryAllowsOrders(category)
		setCategoryForm({
			name: category.name || '',
			allowOrderSelection,
			allowDuplicateOrders: allowOrderSelection ? categoryAllowsDuplicateOrders(category) : false,
			allowedSources: categoryAllowedSources(category),
			requiresApproval: Boolean(category.requiresApproval),
		})
		setCategoryDialogOpen(true)
	}

	const deleteExpenseCategory = async (categoryId) => {
		if (!window.confirm('Ngưng sử dụng khoản chi này?')) return
		try {
			await cashflowService.deleteExpenseCategory(categoryId)
			if (editingCategoryId === categoryId) {
				resetCategoryForm()
			}
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const resetExpenseForm = useCallback(() => {
		const defaultCategory = otherExpenseCategories[0]
		setEditingExpenseId('')
		setExpenseForm({
			type: 'other',
			category: defaultCategory?._id || '',
			bankAccount: categoryAllowsSource(defaultCategory, 'cash') ? DEFAULT_CASH_SOURCE : '',
			amount: '',
			content: defaultCategory?.name || '',
			orderIds: [],
			photos: [],
			existingPhotos: [],
		})
	}, [otherExpenseCategories])

	const openCreateExpense = () => {
		resetExpenseForm()
		setExpenseDialogOpen(true)
	}

	const closeExpenseDialog = () => {
		setExpenseDialogOpen(false)
		resetExpenseForm()
	}

	const startEditExpense = (expense) => {
		setEditingExpenseId(expense._id)
		setExpenseForm({
			type: expense.type || 'shipping',
			category: toId(expense.category) || (expense.type === 'shipping' ? shippingExpenseCategories[0]?._id || '' : ''),
			bankAccount: expense.source === 'cash' ? DEFAULT_CASH_SOURCE : toId(expense.bankAccount) || bankAccounts[0]?._id || '',
			amount: expense.amount ?? '',
			content: expense.content || '',
			orderIds: (expense.orders || []).map(toId).filter(Boolean),
			photos: [],
			existingPhotos: expense.photos || [],
		})
		setExpenseDialogOpen(true)
	}

	const saveExpense = async () => {
		if (!canManageExpense) return
		const content =
			expenseForm.content.trim() ||
			(expenseForm.type === 'shipping' ? SHIPPING_EXPENSE_NAME : selectedExpenseCategory?.name || '')
		if (!content) {
			alert('Vui lòng nhập nội dung khoản chi')
			return
		}
		if (parseMoney(expenseForm.amount) <= 0) {
			alert('Số tiền chi phải lớn hơn 0')
			return
		}
		if (!expenseForm.bankAccount) {
			alert('Vui lòng chọn tài khoản chi')
			return
		}
		if (!expenseForm.category) {
			alert('Vui lòng chọn khoản chi')
			return
		}
		if (!categoryAllowsSource(selectedExpenseCategory, expenseForm.bankAccount === DEFAULT_CASH_SOURCE ? 'cash' : 'bank')) {
			alert(
				`Khoản chi này chỉ được chi bằng ${categoryAllowedSources(selectedExpenseCategory)
					.map((source) => (source === 'cash' ? 'tiền mặt' : 'tài khoản'))
					.join(' hoặc ')}`,
			)
			return
		}
		if (expenseForm.type === 'shipping' && expenseForm.orderIds.length === 0) {
			alert('Vui lòng chọn đơn hàng cho chi phí ship')
			return
		}
		if (
			expenseForm.type !== 'shipping' &&
			expenseForm.orderIds.length > 0 &&
			selectedExpenseCategory &&
			!categoryAllowsOrders(selectedExpenseCategory)
		) {
			alert('Khoản chi này không cho chọn đơn hàng')
			return
		}
		try {
			const formData = new FormData()
			formData.append('storeId', selectedStoreId)
			formData.append('reportDate', date)
			formData.append('type', expenseForm.type)
			formData.append('amount', parseMoney(expenseForm.amount))
			formData.append('content', content)
			formData.append('orderIds', JSON.stringify(expenseForm.orderIds))
			if (expenseForm.bankAccount === DEFAULT_CASH_SOURCE) {
				formData.append('source', 'cash')
			} else {
				formData.append('bankAccount', expenseForm.bankAccount)
			}
			if (expenseForm.category) {
				formData.append('category', expenseForm.category)
			}
			formData.append('existingPhotos', JSON.stringify(expenseForm.existingPhotos || []))
			Array.from(expenseForm.photos || []).forEach((file) => formData.append('photos', file))
			if (editingExpenseId) {
				await cashflowService.updateExpense(editingExpenseId, formData)
			} else {
				await cashflowService.createExpense(formData)
			}
			await loadCashflow()
			setExpenseDialogOpen(false)
			resetExpenseForm()
		} catch (error) {
			showError(error)
		}
	}

	const deleteExpense = async (expenseId) => {
		if (!window.confirm('Xóa khoản chi này?')) return
		try {
			await cashflowService.deleteExpense(expenseId)
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const confirmExpense = async (expenseId) => {
		if (!canApproveExpense) return
		if (!window.confirm('Xác nhận khoản chi này? Sau khi xác nhận khoản chi sẽ được tính vào báo cáo.')) return
		try {
			await cashflowService.confirmExpense(expenseId)
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const resetIncomeForm = useCallback(() => {
		setEditingIncomeId('')
		setIncomeForm({
			receiptType: 'abnormal',
			bankAccount: DEFAULT_CASH_SOURCE,
			amount: '',
			content: '',
			appliesToReportDate: date,
			photos: [],
			existingPhotos: [],
		})
	}, [date])

	const openCreateIncome = () => {
		resetIncomeForm()
		setIncomeDialogOpen(true)
	}

	const closeIncomeDialog = () => {
		setIncomeDialogOpen(false)
		resetIncomeForm()
	}

	const startEditIncome = (income) => {
		setEditingIncomeId(income._id)
		setIncomeForm({
			receiptType: income.receiptType || 'abnormal',
			bankAccount: income.source === 'cash' ? DEFAULT_CASH_SOURCE : toId(income.bankAccount) || bankAccounts[0]?._id || '',
			amount: income.amount ?? '',
			content: income.content || '',
			appliesToReportDate: income.appliesToReportDate || income.reportDate || date,
			photos: [],
			existingPhotos: income.photos || [],
		})
		setIncomeDialogOpen(true)
	}

	const saveIncome = async () => {
		if (!canManageExpense) return
		if (!incomeForm.bankAccount) {
			alert('Vui lòng chọn tài khoản thu')
			return
		}
		if (parseMoney(incomeForm.amount) <= 0) {
			alert('Số tiền thu phải lớn hơn 0')
			return
		}
		if (!incomeForm.content.trim()) {
			alert('Vui lòng nhập nội dung khoản thu')
			return
		}
		if (!incomeForm.appliesToReportDate) {
			alert('Vui lòng chọn ngày gán TK đơn')
			return
		}
		try {
			const formData = new FormData()
			formData.append('storeId', selectedStoreId)
			formData.append('reportDate', date)
			formData.append('receiptType', 'abnormal')
			if (incomeForm.bankAccount === DEFAULT_CASH_SOURCE) {
				formData.append('source', 'cash')
			} else {
				formData.append('bankAccount', incomeForm.bankAccount)
			}
			formData.append('amount', parseMoney(incomeForm.amount))
			formData.append('content', incomeForm.content.trim())
			formData.append('appliesToReportDate', incomeForm.appliesToReportDate)
			formData.append('existingPhotos', JSON.stringify(incomeForm.existingPhotos || []))
			Array.from(incomeForm.photos || []).forEach((file) => formData.append('photos', file))
			if (editingIncomeId) {
				await cashflowService.updateIncome(editingIncomeId, formData)
			} else {
				await cashflowService.createIncome(formData)
			}
			await loadCashflow()
			setIncomeDialogOpen(false)
			resetIncomeForm()
		} catch (error) {
			showError(error)
		}
	}

	const deleteIncome = async (incomeId) => {
		if (!window.confirm('Xóa khoản thu này?')) return
		try {
			await cashflowService.deleteIncome(incomeId)
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const openPaymentRequest = async (order) => {
		if (!canManageExpense) return
		try {
			const accounts = await cashflowService.getPaymentSourceAccounts(order._id, { storeId: selectedStoreId })
			const expectedAccountType = accountTypeForOrder(order)
			const defaultAccount = accounts.find((account) => (account.accountType || 'bank') === expectedAccountType)
			setPaymentSourceAccounts(accounts)
			setPaymentRequestForm({
				orderId: order._id,
				bankAccount: defaultAccount?._id || accounts[0]?._id || '',
				amount: parseMoney(order.totalPrice) + parseMoney(order.shippingTotal),
				content: `Xác nhận thu tiền đơn ${orderCode(order)}`,
			})
			setPaymentRequestDialogOpen(true)
			return true
		} catch (error) {
			showError(error)
			return false
		}
	}

	const closePaymentRequestDialog = () => {
		setPaymentRequestDialogOpen(false)
		setPaymentSourceAccounts([])
		setPaymentRequestForm({ orderId: '', bankAccount: '', amount: '', content: '' })
	}

	const savePaymentRequest = async () => {
		if (!canManageExpense) return
		const selectedPaymentOrder = transferredOrders.find((order) => order._id === paymentRequestForm.orderId)
		const needsSourceAccount = selectedPaymentOrder && orderNeedsManualBankAccount(selectedPaymentOrder)
		if (!paymentRequestForm.orderId || (needsSourceAccount && !paymentRequestForm.bankAccount)) {
			alert(needsSourceAccount ? 'Vui lòng chọn đơn và tài khoản thu' : 'Vui lòng chọn đơn')
			return
		}
		if (parseMoney(paymentRequestForm.amount) <= 0) {
			alert('Số tiền xác nhận phải lớn hơn 0')
			return
		}
		try {
			await cashflowService.createPaymentConfirmation({
				storeId: selectedStoreId,
				reportDate: date,
				orderId: paymentRequestForm.orderId,
				bankAccount: paymentRequestForm.bankAccount,
				amount: parseMoney(paymentRequestForm.amount),
				content: paymentRequestForm.content,
			})
			await loadCashflow()
			closePaymentRequestDialog()
		} catch (error) {
			showError(error)
		}
	}

	const confirmPaymentRequest = async (confirmationId) => {
		try {
			await cashflowService.confirmPaymentConfirmation(confirmationId)
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const rejectPaymentRequest = async (confirmationId) => {
		const reason = window.prompt('Lý do từ chối?') || ''
		try {
			await cashflowService.rejectPaymentConfirmation(confirmationId, { reason })
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const deletePaymentRequest = async (confirmationId) => {
		if (!window.confirm('Xóa yêu cầu xác nhận này?')) return
		try {
			await cashflowService.deletePaymentConfirmation(confirmationId)
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const assignOrderAccount = async (order, accountId) => {
		if (!canManageExpense) return
		try {
			await cashflowService.assignOrderAccount(order._id, {
				storeId: selectedStoreId,
				bankAccount: accountId,
			})
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const linkOrderIncomeOffset = async (order, incomeId) => {
		if (!canManageExpense || !incomeId) return
		try {
			await cashflowService.linkOrderIncomeOffset(order._id, {
				storeId: selectedStoreId,
				incomeId,
			})
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const unlinkOrderIncomeOffset = async (order, incomeId) => {
		if (!canManageExpense || !incomeId) return
		try {
			await cashflowService.unlinkOrderIncomeOffset(order._id, incomeId, {
				storeId: selectedStoreId,
			})
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const updateBankVerification = (accountId, field, value) => {
		setReportForm((prev) => ({
			...prev,
			bankVerifications: {
				...prev.bankVerifications,
				[accountId]: {
					...(prev.bankVerifications[accountId] || {}),
					[field]: value,
				},
			},
		}))
	}

	const updateCashVerification = (field, value) => {
		setReportForm((prev) => ({ ...prev, [field]: value }))
	}

	const addCashFiles = (files) => {
		const newFiles = Array.from(files || [])
		if (newFiles.length === 0) return
		cashPreviewUrlsRef.current = [...cashPreviewUrlsRef.current, ...newFiles.map((file) => URL.createObjectURL(file))]
		setCashPreviewUrls([...cashPreviewUrlsRef.current])
		setReportForm((prev) => ({ ...prev, cashFiles: [...(prev.cashFiles || []), ...newFiles] }))
	}

	const removeCashScreenshot = (item) => {
		if (item.saved) {
			setReportForm((prev) => ({
				...prev,
				cashScreenshots: (prev.cashScreenshots || []).filter((_url, index) => index !== item.index),
			}))
			return
		}
		URL.revokeObjectURL(cashPreviewUrlsRef.current[item.index])
		cashPreviewUrlsRef.current = cashPreviewUrlsRef.current.filter((_url, index) => index !== item.index)
		setCashPreviewUrls([...cashPreviewUrlsRef.current])
		setReportForm((prev) => ({
			...prev,
			cashFiles: (prev.cashFiles || []).filter((_file, index) => index !== item.index),
		}))
	}

	const updateBankOpeningFile = (accountId, file) => {
		if (bankOpeningPreviewUrlsRef.current[accountId]) {
			URL.revokeObjectURL(bankOpeningPreviewUrlsRef.current[accountId])
			delete bankOpeningPreviewUrlsRef.current[accountId]
		}
		if (file) {
			bankOpeningPreviewUrlsRef.current[accountId] = URL.createObjectURL(file)
		}
		setBankOpeningPreviewUrls({ ...bankOpeningPreviewUrlsRef.current })
		setReportForm((prev) => ({
			...prev,
			bankOpeningFiles: {
				...prev.bankOpeningFiles,
				[accountId]: file,
			},
		}))
	}

	const addBankFiles = (accountId, files) => {
		const newFiles = Array.from(files || [])
		if (newFiles.length === 0) return
		bankPreviewUrlsRef.current[accountId] = [
			...(bankPreviewUrlsRef.current[accountId] || []),
			...newFiles.map((file) => URL.createObjectURL(file)),
		]
		setBankPreviewUrls({ ...bankPreviewUrlsRef.current })
		setReportForm((prev) => ({
			...prev,
			bankFiles: { ...prev.bankFiles, [accountId]: [...(prev.bankFiles?.[accountId] || []), ...newFiles] },
		}))
	}

	const removeBankScreenshot = (accountId, item) => {
		if (item.saved) {
			setReportForm((prev) => ({
				...prev,
				bankVerifications: {
					...prev.bankVerifications,
					[accountId]: {
						...(prev.bankVerifications[accountId] || {}),
						screenshots: (prev.bankVerifications[accountId]?.screenshots || []).filter(
							(_url, index) => index !== item.index,
						),
					},
				},
			}))
			return
		}
		URL.revokeObjectURL(bankPreviewUrlsRef.current[accountId][item.index])
		bankPreviewUrlsRef.current[accountId] = bankPreviewUrlsRef.current[accountId].filter(
			(_url, index) => index !== item.index,
		)
		setBankPreviewUrls({ ...bankPreviewUrlsRef.current })
		setReportForm((prev) => ({
			...prev,
			bankFiles: {
				...prev.bankFiles,
				[accountId]: (prev.bankFiles?.[accountId] || []).filter((_file, index) => index !== item.index),
			},
		}))
	}

	// Gộp ảnh đã lưu trên server và ảnh vừa chọn thành 1 danh sách để hiển thị/xóa
	const buildScreenshotItems = (savedUrls, previewUrls) => [
		...(savedUrls || []).map((url, index) => ({ key: `saved-${index}`, url: resolveImageUrl(url), saved: true, index })),
		...(previewUrls || []).map((url, index) => ({ key: `new-${index}`, url, saved: false, index })),
	]

	const getCashScreenshotItems = () =>
		showsSelectedDateImages ? buildScreenshotItems(reportForm.cashScreenshots, cashPreviewUrls) : []

	const getBankScreenshotItems = (accountId, verification) =>
		showsSelectedDateImages ? buildScreenshotItems(verification?.screenshots, bankPreviewUrls[accountId]) : []

	const getBankOpeningScreenshotUrl = (accountId, verification) =>
		showsSelectedDateImages
			? resolveImageUrl(bankOpeningPreviewUrls[accountId] || verification?.openingScreenshot || '')
			: ''

	const getCashOpeningActualBalance = () => parseMoney(summary?.openingBalances?.cash)

	const getAdjustedCashClosingBalance = () =>
		getCashOpeningActualBalance() +
		parseMoney(summary?.closingBalances?.cash) -
		parseMoney(summary?.openingBalances?.cash)

	const cashDifference = () => parseMoney(reportForm.cashActualBalance) - getAdjustedCashClosingBalance()

	const hasCashClosingVerification = () => hasValue(reportForm.cashActualBalance)

	const getOpeningActualBalance = (bankBalance) => {
		const value = reportForm.bankVerifications[bankBalance.bankAccount._id]?.openingActualBalance
		return value === '' || value === undefined || value === null ? bankBalance.openingBalance || 0 : parseMoney(value)
	}

	const getAdjustedClosingBalance = (bankBalance) =>
		getOpeningActualBalance(bankBalance) + parseMoney(bankBalance.income) - parseMoney(bankBalance.expense)

	const openingDifference = (bankBalance) => getOpeningActualBalance(bankBalance) - (bankBalance.openingBalance || 0)

	const hasClosingVerification = (bankBalance) =>
		hasValue(reportForm.bankVerifications[bankBalance.bankAccount._id]?.actualBalance)

	const getActualClosingBalance = (bankBalance) => {
		const value = reportForm.bankVerifications[bankBalance.bankAccount._id]?.actualBalance
		return hasValue(value) ? parseMoney(value) : 0
	}

	const bankDifference = (bankBalance) => {
		const value = reportForm.bankVerifications[bankBalance.bankAccount._id]?.actualBalance
		return parseMoney(value) - getAdjustedClosingBalance(bankBalance)
	}

	const adjustedClosingTotal =
		(summary?.closingBalances?.banks || []).reduce(
			(total, bankBalance) => total + getAdjustedClosingBalance(bankBalance),
			0,
		) + getAdjustedCashClosingBalance()
	const actualClosingTotal =
		(summary?.closingBalances?.banks || []).reduce(
			(total, bankBalance) => total + getActualClosingBalance(bankBalance),
			0,
		) + parseMoney(reportForm.cashActualBalance)
	const totalDifference = actualClosingTotal - adjustedClosingTotal

	const savedAccountVerification = (accountId) =>
		summary?.existingReport?.bankVerifications?.find((item) => toId(item.bankAccount) === accountId)

	const submitCashClosingVerification = async () => {
		if (!canSubmitReport || !summary) return
		if (!hasValue(reportForm.cashActualBalance)) {
			alert('Vui lòng nhập số cuối ngày thực tế của Tiền mặt mặc định')
			return
		}
		if (getCashScreenshotItems().length === 0) {
			alert('Vui lòng tải ảnh đối soát cuối ngày của Tiền mặt mặc định')
			return
		}
		try {
			setSavingAccountId('cash:closing')
			const formData = new FormData()
			formData.append('storeId', selectedStoreId)
			formData.append('reportDate', date)
			formData.append('partial', 'true')
			formData.append('verificationScope', 'cash-closing')
			formData.append('cashOpeningActualBalance', getCashOpeningActualBalance())
			formData.append('cashActualBalance', parseMoney(reportForm.cashActualBalance))
			formData.append('cashExplanation', reportForm.cashExplanation || '')
			formData.append('cashScreenshots', JSON.stringify(reportForm.cashScreenshots || []))
			;(reportForm.cashFiles || []).forEach((file) => formData.append('cashScreenshot', file))
			await cashflowService.submitAccountVerification(formData)
			await loadCashflow()
		} catch (error) {
			showError(error)
		} finally {
			setSavingAccountId('')
		}
	}

	const getReconciliationStatus = () => {
		const accounts = summary?.closingBalances?.banks || []
		const missingCount =
			accounts.filter((bankBalance) => !hasClosingVerification(bankBalance)).length +
			(hasCashClosingVerification() ? 0 : 1)
		if (missingCount > 0) {
			return { color: 'default', label: `Chưa nhập đủ (${missingCount})` }
		}
		return { color: 'success', label: 'Đã đối soát hết' }
	}

	const submitOpeningVerification = async (bankBalance) => {
		if (!canSubmitReport || !summary) return
		const accountId = bankBalance.bankAccount._id
		const verification = reportForm.bankVerifications[accountId] || {}
		if (verification.openingActualBalance === '') {
			alert(`Vui lòng nhập số đầu ngày thực tế của ${bankBalance.bankAccount.name}`)
			return
		}
		try {
			setSavingAccountId(`${accountId}:opening`)
			const formData = new FormData()
			formData.append('storeId', selectedStoreId)
			formData.append('reportDate', date)
			formData.append('partial', 'true')
			formData.append('verificationScope', 'opening')
			formData.append(
				'bankVerifications',
				JSON.stringify([
					{
						bankAccount: accountId,
						openingActualBalance: getOpeningActualBalance(bankBalance),
						openingExplanation: verification.openingExplanation || '',
						openingScreenshot: verification.openingScreenshot || '',
					},
				]),
			)
			if (reportForm.bankOpeningFiles[accountId]) {
				formData.append(`openingBankScreenshot_${accountId}`, reportForm.bankOpeningFiles[accountId])
			}
			await cashflowService.submitAccountVerification(formData)
			await loadCashflow()
		} catch (error) {
			showError(error)
		} finally {
			setSavingAccountId('')
		}
	}

	const submitClosingVerification = async (bankBalance) => {
		if (!canSubmitReport || !summary) return
		const accountId = bankBalance.bankAccount._id
		const verification = reportForm.bankVerifications[accountId] || {}
		if (!hasValue(verification.actualBalance)) {
			alert(`Vui lòng nhập số cuối ngày thực tế của ${bankBalance.bankAccount.name}`)
			return
		}
		if (getBankScreenshotItems(accountId, verification).length === 0) {
			alert(`Vui lòng tải ảnh đối soát cuối ngày của ${bankBalance.bankAccount.name}`)
			return
		}
		try {
			setSavingAccountId(`${accountId}:closing`)
			const formData = new FormData()
			formData.append('storeId', selectedStoreId)
			formData.append('reportDate', date)
			formData.append('partial', 'true')
			formData.append('verificationScope', 'closing')
			formData.append(
				'bankVerifications',
				JSON.stringify([
					{
						bankAccount: accountId,
						actualBalance: parseMoney(verification.actualBalance),
						explanation: verification.explanation || '',
						screenshots: verification.screenshots || [],
					},
				]),
			)
			;(reportForm.bankFiles[accountId] || []).forEach((file) =>
				formData.append(`bankScreenshot_${accountId}`, file),
			)
			await cashflowService.submitAccountVerification(formData)
			await loadCashflow()
		} catch (error) {
			showError(error)
		} finally {
			setSavingAccountId('')
		}
	}
	const savePDF = async (date) => {
		const currentUser = JSON.parse(
			localStorage.getItem("current-user")
		);
	
		const userName = currentUser.name;
		const storeName = selectedStoreName;
		const element = document.querySelector(".cashflow-page");
	
		if (!element) return;
	
		setExporting(true);
	
		try {
			// =========================
			// ĐẢM BẢO DOM ĐÃ RENDER ỔN ĐỊNH
			// =========================
	
			await new Promise((resolve) =>
				requestAnimationFrame(resolve)
			);
	
			// =========================
			// LẤY KÍCH THƯỚC
			// =========================
	
			const width = element.scrollWidth;
			const height = element.scrollHeight;
	
			console.log("PDF width:", width);
			console.log("PDF height:", height);
	
			// =========================
			// TẠO PDF
			// =========================
	
			const worker = html2pdf()
				.set({
					margin: 0,
	
					html2canvas: {
						scale: 4,
						useCORS: true,
						backgroundColor: "#fff",
	
						width,
						height,
	
						scrollX: 0,
						scrollY: 0,
	
						// =========================
						// XỬ LÝ CLONE CỦA HTML2CANVAS
						// =========================
	
						onclone: (clonedDoc) => {
							clonedDoc
								.querySelectorAll(".pdf-ignore")
								.forEach((el) => el.remove());
						},
					},
	
					jsPDF: {
						unit: "px",
						format: [width, height],
					},
	
					pagebreak: {
						mode: [],
					},
				})
				.from(element);
	
			// =========================
			// CHỜ TẠO PDF HOÀN TẤT
			// =========================
	
			await worker.toPdf();
	
			const pdf = await worker.get("pdf");
	
			// =========================
			// LẤY PDF BLOB
			// =========================
	
			const pdfBlob = pdf.output("blob");
	
			console.log("PDF size:", pdfBlob.size);
	
			// =========================
			// TÊN FILE
			// =========================
	
			const filename =
				`${storeName}-${date}-${userName}.pdf`;
	
			// =========================
			// GỬI BACKEND
			// =========================
	
			const formData = new FormData();
	
			formData.append(
				"file_report_pdf",
				pdfBlob,
				filename
			);
	
			await cashflowService.getFilePdf(formData);
	
		} catch (error) {
			console.error("Export PDF failed:", error);
	
		} finally {
			setExporting(false);
		}
	};

	const submitReport = async (status) => {
		if (!canSubmitReport || !summary) return
		if (status === 'submitted') {
			if (!hasValue(reportForm.cashActualBalance)) {
				alert('Vui lòng nhập số cuối ngày thực tế của Tiền mặt mặc định')
				return
			}
			if (unclassifiedIncomeOrderCount > 0) {
				alert(`Còn ${unclassifiedIncomeOrderCount} đơn chưa gán tài khoản thu`)
				return
			}
			if (pendingApprovalExpenseCount > 0) {
				alert(`Còn ${pendingApprovalExpenseCount} khoản chi chờ xác nhận`)
				return
			}
		}
		try {
			const formData = new FormData()
			formData.append('storeId', selectedStoreId)
			formData.append('reportDate', date)
			formData.append('status', status)
			formData.append('action', status === 'submitted' ? 'submit' : 'draft')
			formData.append('cashOpeningActualBalance', getCashOpeningActualBalance())
			if (hasValue(reportForm.cashActualBalance)) {
				formData.append('cashActualBalance', parseMoney(reportForm.cashActualBalance))
			}
			formData.append('cashExplanation', reportForm.cashExplanation || '')
			formData.append('cashScreenshots', JSON.stringify(reportForm.cashScreenshots || []))
			formData.append(
				'bankVerifications',
				JSON.stringify(
					(summary?.closingBalances?.banks || []).map((bankBalance) => {
						const accountId = bankBalance.bankAccount._id
						const verification = reportForm.bankVerifications[accountId] || {}
						return {
							bankAccount: accountId,
							openingActualBalance: getOpeningActualBalance(bankBalance),
							openingExplanation: verification.openingExplanation || '',
							openingScreenshot: verification.openingScreenshot || '',
							actualBalance: hasValue(verification.actualBalance) ? parseMoney(verification.actualBalance) : '',
							explanation: verification.explanation || '',
							screenshots: verification.screenshots || [],
						}
					}),
				),
			)
			Object.entries(reportForm.bankOpeningFiles || {}).forEach(([accountId, file]) => {
				if (file) formData.append(`openingBankScreenshot_${accountId}`, file)
			})
			Object.entries(reportForm.bankFiles || {}).forEach(([accountId, files]) => {
				;(files || []).forEach((file) => formData.append(`bankScreenshot_${accountId}`, file))
			})
			;(reportForm.cashFiles || []).forEach((file) => formData.append('cashScreenshot', file))
			await cashflowService.submitDailyReport(formData)
			if (status === 'submitted') await savePDF(date)
			await loadCashflow()
		} catch (error) {
			showError(error)
		}
	}

	const renderStoreDateFilter = () => (
		<StoreDateFilter
			date={date}
			isAdminLike={isAdminLike}
			loadCashflow={loadCashflow}
			selectedStoreId={selectedStoreId}
			selectedStoreName={selectedStoreName}
			setDate={setDate}
			setSelectedStoreId={setSelectedStoreId}
			stores={stores}
		/>
	)

	const renderConfig = (section) => (
		<ConfigTab
			bankAccounts={allBankAccounts}
			bankForm={bankForm}
			canManageAccounts={canManageAccounts}
			canManageConfig={canManageConfig}
			categoryDialogOpen={categoryDialogOpen}
			categoryForm={categoryForm}
			closeCategoryDialog={closeCategoryDialog}
			createDefaultExpenseCategories={createDefaultExpenseCategories}
			deleteBankAccount={deleteBankAccount}
			deleteExpenseCategory={deleteExpenseCategory}
			editingCategoryId={editingCategoryId}
			expenseCategories={expenseCategories}
			hasDefaultExpenseCategory={hasDefaultExpenseCategory}
			openCreateExpenseCategory={openCreateExpenseCategory}
			saveBankAccount={saveBankAccount}
			saveExpenseCategory={saveExpenseCategory}
			setBankAccountActive={setBankAccountActive}
			section={section}
			setBankForm={setBankForm}
			setCategoryForm={setCategoryForm}
			startEditExpenseCategory={startEditExpenseCategory}
		/>
	)

	const renderIncomes = () => (
		<IncomesTab
			bankAccounts={bankAccounts}
			canManageIncome={canManageExpense}
			closeIncomeDialog={closeIncomeDialog}
			deleteIncome={deleteIncome}
			editingIncomeId={editingIncomeId}
			incomeDialogOpen={incomeDialogOpen}
			incomeForm={incomeForm}
			openCreateIncome={openCreateIncome}
			saveIncome={saveIncome}
			setIncomeForm={setIncomeForm}
			startEditIncome={startEditIncome}
			summary={summary}
		/>
	)

	const renderPaymentConfirmations = () => (
		<PaymentConfirmationsTab
			canManagePayment={canManageExpense}
			confirmPaymentRequest={confirmPaymentRequest}
			deletePaymentRequest={deletePaymentRequest}
			rejectPaymentRequest={rejectPaymentRequest}
			summary={summary}
		/>
	)

	const renderPaymentRequestDialog = () => (
		<PaymentRequestDialog
			canManagePayment={canManageExpense}
			closePaymentRequestDialog={closePaymentRequestDialog}
			paymentRequestDialogOpen={paymentRequestDialogOpen}
			paymentRequestForm={paymentRequestForm}
			paymentSourceAccounts={paymentSourceAccounts}
			savePaymentRequest={savePaymentRequest}
			setPaymentRequestForm={setPaymentRequestForm}
			transferredOrders={transferredOrders}
		/>
	)

	const renderOrderAccountAssignment = () => (
		<OrderAccountAssignment
			assignOrderAccount={assignOrderAccount}
			bankAccounts={bankAccounts}
			canManageExpense={canManageExpense}
			classificationOrders={classificationOrders}
			confirmedOutgoingPaymentByOrderId={confirmedOutgoingPaymentByOrderId}
			getAccountsForOrder={getAccountsForOrder}
			incomeOffsetCandidates={summary?.income?.offsetCandidates || []}
			incomeOrders={incomeOrders}
			linkOrderIncomeOffset={linkOrderIncomeOffset}
			openPaymentRequest={openPaymentRequest}
			prepaidOffsetByOrder={prepaidOffsetByOrder}
			setImageViewer={setImageViewer}
			unclassifiedIncomeOrderCount={unclassifiedIncomeOrderCount}
			unlinkOrderIncomeOffset={unlinkOrderIncomeOffset}
		/>
	)

	const renderExpenses = () => (
		<ExpensesTab
			bankAccounts={bankAccounts}
			canApproveExpense={canApproveExpense}
			canManageExpense={canManageExpense}
			cashoutExpenseCategories={cashoutExpenseCategories}
			closeExpenseDialog={closeExpenseDialog}
			confirmExpense={confirmExpense}
			deleteExpense={deleteExpense}
			editingExpenseId={editingExpenseId}
			expenseDialogOpen={expenseDialogOpen}
			expenseForm={expenseForm}
			expenseOrderOptions={expenseOrderOptions}
			getLockedOrderExpense={getLockedOrderExpense}
			openCreateExpense={openCreateExpense}
			otherExpenseCategories={otherExpenseCategories}
			saveExpense={saveExpense}
			selectedExpenseOrders={selectedExpenseOrders}
			selectedShippingIncome={selectedShippingIncome}
			selectedExpenseCategory={selectedExpenseCategory}
			setImageViewer={setImageViewer}
			setExpenseForm={setExpenseForm}
			shippingExpenseCategories={shippingExpenseCategories}
			startEditExpense={startEditExpense}
			summary={summary}
		/>
	)

	const renderReport = () => (
		<ReportTab
			actualClosingTotal={actualClosingTotal}
			adjustedClosingTotal={adjustedClosingTotal}
			addBankFiles={addBankFiles}
			addCashFiles={addCashFiles}
			bankDifference={bankDifference}
			canSubmitReport={canSubmitReport}
			cashDifference={cashDifference}
			getAdjustedClosingBalance={getAdjustedClosingBalance}
			getAdjustedCashClosingBalance={getAdjustedCashClosingBalance}
			getBankOpeningScreenshotUrl={getBankOpeningScreenshotUrl}
			getBankScreenshotItems={getBankScreenshotItems}
			getCashScreenshotItems={getCashScreenshotItems}
			getReconciliationStatus={getReconciliationStatus}
			isDateLocked={isDateLocked}
			openingDifference={openingDifference}
			orderStatistics={orderStatistics}
			pendingApprovalExpenseCount={pendingApprovalExpenseCount}
			removeBankScreenshot={removeBankScreenshot}
			removeCashScreenshot={removeCashScreenshot}
			reportForm={reportForm}
			savedAccountVerification={savedAccountVerification}
			savingAccountId={savingAccountId}
			setImageViewer={setImageViewer}
			submitCashClosingVerification={submitCashClosingVerification}
			submitClosingVerification={submitClosingVerification}
			submitOpeningVerification={submitOpeningVerification}
			submitReport={submitReport}
			summary={summary}
			totalDifference={totalDifference}
			unclassifiedIncomeOrderCount={unclassifiedIncomeOrderCount}
			updateBankOpeningFile={updateBankOpeningFile}
			updateBankVerification={updateBankVerification}
			updateCashVerification={updateCashVerification}
		/>
	)

	const renderImageViewer = () => <ImageViewerDialog imageViewer={imageViewer} setImageViewer={setImageViewer} />
	const cashflowTabs = [
		{ label: 'Gán TK đơn', render: renderOrderAccountAssignment },
		{ label: 'Xác nhận liên CH', render: renderPaymentConfirmations },
		{ label: 'Khoản chi', render: renderExpenses },
		{ label: 'Khoản thu bất thường', render: renderIncomes },
		{ label: 'Báo cáo ngày', render: renderReport },
		...(canManageAccounts ? [{ label: 'Cấu hình Tài khoản', render: () => renderConfig('accounts') }] : []),
		...(canManageConfig ? [{ label: 'Cấu hình khoản chi', render: () => renderConfig('categories') }] : []),
	]
	const activeTabIndex = cashflowTabs[tab] ? tab : 0
	const activeTab = cashflowTabs[activeTabIndex]

	useEffect(() => {
		if (tab !== activeTabIndex) {
			setTab(activeTabIndex)
		}
	}, [activeTabIndex, tab])

	return (
		<UserLayout>
			 {exporting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            background: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            cursor: "wait",
          }}
        >
          <div>Đang tạo PDF...</div>
        </div>
      )}
			<Box
				className='cashflow-page'
				sx={{
					width: '100%',
					maxWidth: '100%',
					minWidth: 0,
					mx: 0,
					overflowX: 'hidden',
					boxSizing: 'border-box',
					p: { xs: 1.25, md: 2.5 },
				}}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					alignItems={{ xs: 'flex-start', sm: 'center' }}
					justifyContent='space-between'
					spacing={1.5}
					sx={{ mb: 1.75 }}>
					<Stack direction='row' alignItems='center' spacing={1.25}>
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: '12px',
								display: 'grid',
								placeItems: 'center',
								color: mainGreen,
								background: 'rgba(0, 136, 72, 0.1)',
							}}>
							<AccountBalanceIcon sx={{ fontSize: 28 }} />
						</Box>
						<Box>
							<Typography sx={{ fontWeight: 900, fontSize: { xs: 23, md: 26 }, lineHeight: 1.1 }}>Thu chi</Typography>
							<Typography sx={{ color: '#637381', fontSize: 13, fontWeight: 700 }}>{selectedStoreName}</Typography>
						</Box>
					</Stack>
				</Stack>
				{renderStoreDateFilter()}
				{isDateLocked && (
					<Alert severity='warning' sx={{ mb: 2 }}>
						Ngày {date} đã qua nên mọi thao tác thu chi của ngày này đã bị khóa, chỉ còn xem lại số liệu.
					</Alert>
				)}
				<Paper
					elevation={0}
					sx={{
						border: '1px solid #DDE7DF',
						borderRadius: '14px',
						mb: 2,
						background: 'rgba(255, 255, 255, 0.92)',
						boxShadow: '0 12px 34px rgba(24, 64, 39, 0.05)',
					}}>
					<Tabs
						value={activeTabIndex}
						onChange={(_event, nextTab) => setTab(nextTab)}
						variant='scrollable'
						scrollButtons='auto'
						sx={{ px: { xs: 0.75, md: 1 } }}>
						{cashflowTabs.map((cashflowTab) => (
							<Tab key={cashflowTab.label} label={cashflowTab.label} />
						))}
					</Tabs>
				</Paper>
				{isRefreshing && (
					<LinearProgress
						sx={{
							mb: 1.5,
							height: 4,
							borderRadius: 2,
							background: 'rgba(0, 136, 72, 0.15)',
							'& .MuiLinearProgress-bar': { background: mainGreen },
						}}
					/>
				)}
				{showFullPageLoading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
						<CircularProgress sx={{ color: mainGreen }} />
					</Box>
				) : (
					<>{activeTab.render()}</>
				)}
			</Box>
			{renderPaymentRequestDialog()}
			{renderImageViewer()}
		</UserLayout>
	)
}
